import UseContext from "../../../../../hooks/useContext";
import useModal from "../../../useModal";
import Modalheader from "../../../components/header";
import React, { useEffect, useMemo, useState } from "react";
import ModalFooter from "../../../components/footer";
import Button from "../../../../button";
import ModalBody from "../../../components/body";
import Banner from "../../../../banner/banner";
import CheckboxOptionCard from "../../../../checkboxOptionCard/CheckboxOptionCard";
import {
  Calendar,
  ChevronRight,
  Lightning,
  Signer,
  Warning,
  WarningCircle,
} from "../../../../../assets/icons";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale";
import ApiDatabase from "server";
import { toast } from "react-toastify";

registerLocale("fr-FR", fr);
import { ScheduleChangeBeforeAfter } from "../../../../scheduleCompact/ScheduleCompactDisplay";
import { dateToIsoString, dateWordShort, parseCalendarDate } from "../../../../../helper/helper";
import {
  changeNote,
  formatChangeValue,
  localizedChangeLabel,
} from "./missionChangeTexts";
import { useRiskyPositions } from "components/SlideOver/dataSlideOver/data/slideOverAddMission/missionTemplate/missionTemplateRiskyPositions";
import StepPills from "components/stepPills/StepPills";
import StyledInput from "components/styledInput/StyledInput";
import {
  getInterruptEndDateError,
  getReplacementRange,
  isWithinTrialPeriod,
} from "helper/missionInterruptDates";
import { isContractImpactingChange } from "helper/missionFieldImpact";
import { closureReasonsFor } from "helper/closureReasons";
import {
  CONTRACT_CLOSURE_INCOMPLETE,
  isContractClosureIncomplete,
} from "helper/contractClosure";
import {
  describeContractOutcome,
  describeDatePatch,
  describeStartChange,
  isOnlyTheInitialContract,
  prolongationCount,
  contractStateOf,
  CONTRACT_STATE,
} from "helper/missionContractOutcome";

const FIELD_LABELS = {
  qualification: "Qualification",
  period: "Période",
  schedule: "Planning",
  salary: "Rémunération",
  coefficient: "Coefficient de facturation",
  address: "Adresse",
  reason: "Motif",
  licences: "Permis",
  languages: "Langues",
  tools: "Matériel",
  reference: "Référence",
  other: "Informations pour le candidat",
  other_interim: "Informations pour l'agence",
};

function formatPeriod(val) {
  if (!val) return "";
  if (val.start && val.end) {
    return `${dateWordShort(val.start)} → ${dateWordShort(val.end)}`;
  }
  return String(val);
}

// JBWEB-1089: the two steps of a confirmation that opens a prolongation request.
const PROLONGATION_STEPS = [
  { key: "recap", label: "Récapitulatif" },
  { key: "prolongation", label: "Prolongation" },
];

// JBWEB-1089: the two steps of a confirmation that ends the mission early.
// JBWEB-1089: replacing the temp worker opens a third step, where the dates of
// the mission that takes over are settled.
const REPLACEMENT_DATES_STEP = { key: "dates", label: "Dates" };

const INTERRUPTION_STEPS = [
  { key: "recap", label: "Récapitulatif" },
  { key: "interruption", label: "Interruption" },
];

// JBWEB-1089: the two steps of a duplication — what changes, then the dates
// the new mission will run on.
const DUPLICATE_STEPS = [
  { key: "recap", label: "Récapitulatif" },
  { key: "dates", label: "Dates" },
];

/**
 * The card the design draws for a change that only needs confirming, named by
 * what the change actually does to the contract.
 */
const CHANGE_CARDS = {
  amendment: { icon: <Signer wh={16} color="#374151" />, title: "Avenant requis" },
  patch: {
    icon: <Signer wh={16} color="#374151" />,
    title: "Contrat BeSTT mis à jour",
  },
  immediate: {
    icon: <Lightning wh={16} color="#374151" />,
    title: "Appliqué immédiatement",
  },
  refused: {
    tone: "danger",
    icon: <Warning wh={16} color="#DC2626" />,
    title: "Modification impossible",
  },
};

const cardKeyForOutcome = (outcome) =>
  outcome?.valueAmendment ? "amendment" : outcome?.patch ? "patch" : "immediate";

// JBWEB-1089: who asks to end a mission early. Each side has its own closure
// reasons, in the database, each tied to the BeSTT motif that will close the
// contract — the value is their type.
const INTERRUPTION_REQUESTERS = [
  { value: "closure_company", label: "Entreprise" },
  { value: "closure_tempworker", label: "Intérimaire" },
];
const CLOSURE_REASON_TYPES = INTERRUPTION_REQUESTERS.map(
  (requester) => requester.value
);

// Why a change can no longer be applied to this mission, as the red card says
// it. The api refuses the same three (checkPeriodChangeConstraints).
const DUPLICATE_REASONS = {
  max_amendments:
    "Le nombre maximum d'avenants de prolongation pour cette mission est atteint (2/2). Ce changement ne peut plus être appliqué par avenant.",
  flexibility_exhausted:
    "La souplesse de cette mission a déjà été utilisée. Ce changement ne peut plus être appliqué par avenant.",
  max_duration:
    "La mission dépasserait 18 mois. Ce changement ne peut pas être appliqué à cette mission.",
};

/** The old value struck through, then the new one — "-" when it was emptied. */
function BeforeAfter({ before, after }) {
  const hasBefore = before !== null && before !== undefined && before !== "";
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {hasBefore && (
        <>
          <span className="line-through text-gray-500">{before}</span>
          <ChevronRight wh={14} color="#6B7280" />
        </>
      )}
      <span className="text-gray-900">
        {after === null || after === undefined || after === "" ? "-" : after}
      </span>
    </div>
  );
}

/**
 * One change as the design lists it: its label, then its values. A period whose
 * start did not move reads as its end date alone — which is what a prolongation
 * changes.
 */
function ChangeRow({ change, mission, className = "" }) {
  const isPeriod = change.field === "period";
  const startMoved =
    isPeriod &&
    Boolean(change.before?.start && change.after?.start) &&
    dateWordShort(change.before.start) !== dateWordShort(change.after.start);

  const label = isPeriod
    ? startMoved
      ? "Période"
      : "Date de fin"
    : localizedChangeLabel(change.field) ||
      FIELD_LABELS[change.field] ||
      change.field;
  const note = changeNote(change, mission);
  const riskyPositions = useRiskyPositions({
    skip: change.field !== "riskyPosition",
  });

  const valueOf = (value) => {
    // A null coefficient is a reset to the company value, which has a name.
    if (change.field === "coefficient")
      return formatChangeValue(change.field, value);
    if (value === null || value === undefined) return null;
    if (!isPeriod) return formatChangeValue(change.field, value, riskyPositions);
    if (startMoved) return formatPeriod(value);
    return value.end ? dateWordShort(value.end) : null;
  };

  return (
    <div className={`flex flex-col gap-1 text-sm ${className}`}>
      <span className="text-gray-700">{label}</span>
      {change.field === "schedule" ? (
        <ScheduleChangeBeforeAfter before={change.before} after={change.after} />
      ) : (
        <BeforeAfter before={valueOf(change.before)} after={valueOf(change.after)} />
      )}
      {note && <span className="text-xs text-gray-500">{note}</span>}
    </div>
  );
}

/**
 * The changes of an operation under the one thing they add up to — "Avenant
 * requis", "Appliqué immédiatement", "Nouvelle mission requise" — as the design
 * groups them. The danger tone is for a change that cannot be applied at all.
 */
function ChangesCard({
  icon,
  title,
  description,
  tone = "neutral",
  changes,
  mission,
}) {
  return (
    <div
      className={`${
        tone === "danger" ? "bg-red-50" : "bg-gray-100"
      } rounded-md p-1.5 flex flex-col`}
    >
      <div className="flex items-start gap-2 px-2.5 py-2 text-sm">
        <span className="shrink-0 mt-0.5 flex">{icon}</span>
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-gray-700">{title}</span>
          {description && (
            <span className="text-xs text-gray-600">{description}</span>
          )}
        </div>
      </div>
      <div className="bg-white rounded flex flex-col divide-y divide-gray-100">
        {changes.map((change, index) => (
          <ChangeRow
            key={index}
            change={change}
            mission={mission}
            className="px-3.5 py-3"
          />
        ))}
      </div>
    </div>
  );
}

/**
 * ChangesCard's outline while the outcome is still unknown, one row per change,
 * so the modal does not change size once it is.
 */
function ChangesCardSkeleton({ rows = 1 }) {
  return (
    <div className="bg-gray-100 rounded-md p-1.5 flex flex-col">
      <div className="flex items-start gap-2 px-2.5 py-2">
        <span className="shrink-0 mt-0.5 w-4 h-4 skeleton-bar" />
        <div className="flex flex-col gap-1.5 flex-1">
          <span className="h-4 w-40 skeleton-bar" />
          <span className="h-3 w-56 skeleton-bar" />
        </div>
      </div>
      <div className="bg-white rounded flex flex-col divide-y divide-gray-100">
        {Array.from({ length: Math.max(rows, 1) }, (_, index) => (
          <div key={index} className="flex flex-col gap-2 px-3.5 py-3">
            <span className="h-4 w-20 skeleton-bar" />
            <span className="h-4 w-64 skeleton-bar" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A date, drawn as the interruption modal draws its own. */
function DateField({ label, value, onChange, minDate, error, hint }) {
  return (
    <label className={"ft-sm text-gray-500 block"}>
      {label}
      <div className={"relative mt-1"}>
        <DatePicker
          dateFormat="dd/MM/yyyy"
          locale="fr-FR"
          selected={value}
          onChange={onChange}
          minDate={minDate}
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          popperPlacement="auto"
          popperProps={{ strategy: "fixed" }}
          popperClassName="z-[9999]"
          className="w-full h-10 pl-10 pr-3 ft-b border rounded-lg text-left text-[14px] cursor-pointer"
        />
        <div
          className={
            "absolute inset-y-0 left-0 flex items-center px-2.5 pointer-events-none"
          }
        >
          <Calendar wh={18} color="#6B7280" />
        </div>
      </div>
      {hint && !error && (
        <div className={"text-xs text-gray-500 mt-1"}>{hint}</div>
      )}
      {error && (
        <div className={"text-red-500 flex items-center gap-1 text-xs mt-1"}>
          <WarningCircle wh={16} color="#EF4444" />
          {error}
        </div>
      )}
    </label>
  );
}

// Calendar day of a date, whatever it was stored or sent as.
function calendarDayOf(value) {
  const d = parseCalendarDate(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Whether an earlier end falls in the negative souplesse — the window the
 * contract already allows around its term. There the term moves without a
 * rupture. Not for a replacement (imprecise term), nor once the souplesse has
 * been used. Twin of the api.
 */
function isWithinNegativeFlexibility(mission, newEnd) {
  const re = mission.replacement_employee;
  if (re && (re.firstname || re.lastname || re.poste || re.description)) {
    return false;
  }
  if (!mission.flexibility?.start || mission.flexibility.used) return false;
  const day = calendarDayOf(newEnd);
  return (
    day >= calendarDayOf(mission.flexibility.start) &&
    day < calendarDayOf(mission.duration?.dateend)
  );
}

function computeCheckResult(mission, changes) {
  if (!mission || !changes?.length) return null;

  const hasContract =
    mission.contract_tempworker?.length > 0 &&
    mission.contract_tempworker.some((c) => c.id_bestt);
  const isAssigned = !!mission.user_assigned;
  const hasPeriodChange = changes.some((c) => c.field === "period");

  if (!isAssigned || !hasContract) {
    return {
      canConfirm: true,
      noContract: isAssigned && !hasContract,
    };
  }

  // Le contrat n'est plus refait : il est patché, ou amendé. Lequel des deux se
  // décide ici plutôt que de laisser le doute à l'admin.
  if (!hasPeriodChange) {
    const outcome = describeContractOutcome({ mission, changes });

    return {
      canConfirm: true,
      card: cardKeyForOutcome(outcome),
      banner: outcome.message
        ? { type: "info", message: outcome.message }
        : undefined,
    };
  }

  const periodChange = changes.find((c) => c.field === "period");
  const newDateEnd = parseCalendarDate(periodChange.after.end);

  // Nothing signed, the initial contract alone, no hours imported: the contract
  // is corrected on BeSTT rather than amended — start or end. Twin of the api.
  const canPatchPeriod =
    contractStateOf(mission) !== CONTRACT_STATE.SIGNED &&
    isOnlyTheInitialContract(mission) &&
    !mission.has_imported_bestt_hours;

  // JBWEB-1144 : avancer la fin d'une mission attribuée est juridiquement une
  // rupture anticipée. Ce n'est pas une modification de champ, et l'api la
  // refuse — c'est le formulaire d'interruption qui prend le relais.
  const currentEnd = parseCalendarDate(mission.duration?.dateend);
  if (newDateEnd < currentEnd) {
    // Within the negative souplesse the term moves without a rupture: the
    // mission is updated in Jobbiz, the BeSTT contract is not. Twin of the api.
    if (isWithinNegativeFlexibility(mission, periodChange.after.end)) {
      return { canConfirm: true, negativeFlexibility: true };
    }
    // Before the mission starts there is nothing to break off — but BeSTT still
    // has to carry the new end, so this is only a correction while the contract
    // can be patched: unsigned, alone, without hours and without a DPAE. An
    // amendment reaching past the new end, or anything engaged, makes it a
    // rupture like any other. Twin of the api.
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (today < calendarDayOf(mission.duration?.datestart) && canPatchPeriod) {
      return { canConfirm: true, notStarted: true, card: "patch" };
    }
    return {
      canConfirm: false,
      requiresInterruption: true,
      banner: {
        type: "alert",
        message:
          "Raccourcir la fin de mission d'un salarié attribué est juridiquement une rupture anticipée. Un motif est obligatoire.",
      },
    };
  }

  // A new start is no prolongation: by decision, an amendment of value carrying
  // the new period. Both ends at once would be two amendments — one of them
  // awaiting the temp worker — so it is done in two steps. Twin of the api.
  const startMoved =
    Boolean(periodChange.after?.start) &&
    calendarDayOf(periodChange.after.start) !==
      calendarDayOf(mission.duration?.datestart);

  if (startMoved) {
    const newDateStart = parseCalendarDate(periodChange.after.start);
    const monthsSpanned =
      (newDateEnd.getFullYear() - newDateStart.getFullYear()) * 12 +
      (newDateEnd.getMonth() - newDateStart.getMonth());
    if (monthsSpanned > 18) {
      return {
        canConfirm: false,
        canDuplicate: true,
        duplicateReason: "max_duration",
        banner: {
          type: "alert",
          message:
            "Les dates de la mission dépassent les 18 mois : la modification n'est pas possible.",
        },
      };
    }

    if (canPatchPeriod) {
      return {
        canConfirm: true,
        card: "patch",
        banner: { type: "info", message: describeDatePatch(mission) },
      };
    }

    if (
      calendarDayOf(periodChange.after.end) !==
      calendarDayOf(mission.duration?.dateend)
    ) {
      return {
        canConfirm: false,
        banner: {
          type: "alert",
          message:
            "Le contrat est déjà engagé : modifiez d'abord la date de début, puis prolongez la mission.",
        },
      };
    }

    return {
      canConfirm: true,
      card: "amendment",
      banner: {
        type: "info",
        message: describeStartChange(
          mission,
          dateWordShort(periodChange.after.start)
        ),
      },
    };
  }

  // JBWEB-1144 : une mission de remplacement n'a pas de terme fixe — elle finit
  // au retour du salarié remplacé. Elle se prolonge donc autant que nécessaire,
  // là où les autres motifs plafonnent à deux avenants, et la souplesse stockée
  // ne veut rien dire pour elle : elle a été calculée comme si le terme était
  // précis. Les 18 mois, eux, valent pour tout le monde.
  const re = mission.replacement_employee;
  const isReplacement = Boolean(
    re && (re.firstname || re.lastname || re.poste || re.description)
  );

  if (!isReplacement) {
    if (mission.flexibility?.used) {
      return {
        canConfirm: false,
        canDuplicate: true,
        duplicateReason: "flexibility_exhausted",
        banner: {
          type: "alert",
          message:
            "La souplesse a déjà été utilisée, la date de fin ne peut pas être modifiée.",
        },
      };
    }

    if (mission.flexibility?.start && mission.flexibility?.end) {
      const flexEnd = parseCalendarDate(mission.flexibility.end);
      if (newDateEnd <= flexEnd) {
        return {
          canConfirm: true,
          card: "immediate",
          banner: {
            type: "info",
            message:
              "La date de fin se trouvant dans la souplesse positive, aucun avenant ne sera créé pour cette mission. La souplesse sera marquée comme utilisée.",
          },
        };
      }
    }

    // Le plafond porte sur les prolongations, pas sur les avenants en général :
    // un avenant de valeur ne renouvelle pas le terme.
    if (prolongationCount(mission) >= 2) {
      return {
        canConfirm: false,
        canDuplicate: true,
        duplicateReason: "max_amendments",
        banner: {
          type: "alert",
          borderless: true,
          message:
            "Le nombre maximum d'avenants (2) a été atteint. Vous pouvez dupliquer la mission.",
        },
      };
    }
  }

  const missionStart = parseCalendarDate(mission.duration?.datestart);
  const monthsDiff =
    (newDateEnd.getFullYear() - missionStart.getFullYear()) * 12 +
    (newDateEnd.getMonth() - missionStart.getMonth());
  if (monthsDiff > 18) {
    return {
      canConfirm: false,
      canDuplicate: true,
      duplicateReason: "max_duration",
      banner: {
        type: "alert",
        message:
          "Les dates de la mission dépassent les 18 mois, la date de fin ne peut pas être modifiée.",
      },
    };
  }

  // Nothing signed, the initial contract alone, no hours imported: nobody is
  // bound yet, so the contract is corrected rather than extended — its dates
  // are patched on BeSTT, never regenerated. The replacement follows the same
  // rule: its exemption is from the caps, not from this. Twin of the api.
  if (canPatchPeriod) {
    return {
      canConfirm: true,
      card: "patch",
      banner: { type: "info", message: describeDatePatch(mission) },
    };
  }

  if (isReplacement) {
    return {
      canConfirm: true,
      isAmendmentRequest: true,
      isReplacement: true,
      banner: {
        type: "info",
        message:
          "Le motif étant un remplacement, la mission peut être prolongée autant que nécessaire : un avenant de prolongation sera créé.",
      },
    };
  }

  return { canConfirm: true, isAmendmentRequest: true };
}

const ModalMissionDetailSubmit = () => {
  const { toggle } = useModal();
  const {
    listChangesMissionDetail,
    isChangesNeedsNewContractMissionDetail,
    infoMission,
    objID,
    setObjID,
    setModalDataReturn,
    updateMissionStatusLocal,
  } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  // JBWEB-1144 : décochée par défaut — la demande de prolongation est le cas
  // normal, l'accord donné de vive voix est l'exception.
  const [tempWorkerAlreadyAgreed, setTempWorkerAlreadyAgreed] = useState(false);
  const [step, setStep] = useState("recap");
  const [interruptionRequester, setInterruptionRequester] = useState("");
  const [interruptionReason, setInterruptionReason] = useState("");
  const [interruptionComment, setInterruptionComment] = useState("");
  const [replaceTempWorker, setReplaceTempWorker] = useState(false);
  // Left null until touched: the defaults follow the interruption date, which
  // the admin can still be changing on the step before.
  const [replacementStart, setReplacementStart] = useState(null);
  const [replacementEnd, setReplacementEnd] = useState(null);
  const [closureReasons, setClosureReasons] = useState([]);
  // Left null until the admin touches them: the defaults below follow the
  // mission, which the recap step can still be showing.
  const [duplicateStart, setDuplicateStart] = useState(null);
  const [duplicateEnd, setDuplicateEnd] = useState(null);
  const token = localStorage.getItem("xsrfToken");

  // JBWEB-1089: a contract nobody has signed is engaged all the same once its
  // DPAE has gone out, and only BeSTT knows that. Asked when the modal opens —
  // and only when the signatures leave it open and a change could depend on it.
  const needsDpae = useMemo(() => {
    const state = contractStateOf(infoMission);
    const unsettled =
      state === CONTRACT_STATE.GENERATED || state === CONTRACT_STATE.SENT;
    return (
      unsettled &&
      (listChangesMissionDetail || []).some(
        (change) =>
          isContractImpactingChange(change) || change.field === "schedule"
      )
    );
  }, [infoMission, listChangesMissionDetail]);
  const [dpaeTransmitted, setDpaeTransmitted] = useState(null);

  useEffect(() => {
    if (!needsDpae) return;
    ApiDatabase.getMissionDpae(
      { token, idMission: infoMission?._id },
      (data) => setDpaeTransmitted(Boolean(data?.dpaeTransmitted)),
      // Unreadable counts as not sent, as on the api.
      () => setDpaeTransmitted(false)
    );
  }, [needsDpae, infoMission?._id]);

  // The mission as the rules must read it: a DPAE, once known, marked on the
  // initial contract the way the api stores it.
  const engagedMission = useMemo(() => {
    if (!dpaeTransmitted || !infoMission) return infoMission;
    const mark = (entries = []) =>
      entries.map((entry) =>
        entry?.id_bestt && !((entry.avenant_number || 0) > 0)
          ? { ...entry, dpae_transmitted: true }
          : entry
      );
    return {
      ...infoMission,
      contract_tempworker: mark(infoMission.contract_tempworker),
      contract_company: mark(infoMission.contract_company),
    };
  }, [infoMission, dpaeTransmitted]);

  const dpaePending = needsDpae && dpaeTransmitted === null;

  const checkResult = useMemo(
    () =>
      // Nothing can be decided before the DPAE is known: the modal shows its
      // loading state meanwhile, and nothing can be confirmed.
      dpaePending
        ? { canConfirm: false }
        : computeCheckResult(engagedMission, listChangesMissionDetail),
    [dpaePending, engagedMission, listChangesMissionDetail]
  );

  const canConfirm = checkResult?.canConfirm !== false;
  const canDuplicate = checkResult?.canDuplicate === true;
  const requiresInterruption = checkResult?.requiresInterruption === true;

  // The closure reasons are only needed when the change ends the mission early.
  useEffect(() => {
    if (!requiresInterruption) return;
    ApiDatabase.getReasonsMissionSuspension(
      { type: CLOSURE_REASON_TYPES.join(",") },
      (data) => setClosureReasons(data || []),
      () => setClosureReasons([])
    );
  }, [requiresInterruption]);
  const banner = checkResult?.banner;

  const handleSubmit = () => {
    setIsLoading(true);
    ApiDatabase.postMissionDetail(
      {
        token,
        idMission: objID,
        // Required by the api; nothing asks for a generation from here any more.
        generateContract: Boolean(isChangesNeedsNewContractMissionDetail),
        tempWorkerAlreadyAgreed,
        changes: listChangesMissionDetail,
      },
      (data) => {
        toast.success(data.message || data.error || data.success);
        if (data.idContract) {
          setObjID(data.idContract);
        } else {
          setObjID("");
        }
        setModalDataReturn("mission updated");
        toggle();
      },
      (err) => {
        toast.error(err.response?.data?.message || err.response?.data?.error);
        setModalDataReturn("mission updated");
        toggle();
      }
    );
  };

  // The new mission takes over where this one stops, and runs to the end that
  // was asked for — the dates the admin came with. They are proposed, not
  // imposed: the Dates step is where they are settled.
  const duplicateDefaults = useMemo(() => {
    const originalEnd = parseCalendarDate(infoMission?.duration?.dateend);
    const start = Number.isNaN(originalEnd?.getTime()) ? new Date() : new Date(originalEnd);
    start.setDate(start.getDate() + 1);

    const requestedEnd = listChangesMissionDetail?.find(
      (change) => change.field === "period"
    )?.after?.end;
    const end = requestedEnd ? parseCalendarDate(requestedEnd) : null;

    return {
      start,
      end: end && !Number.isNaN(end.getTime()) ? end : new Date(start),
    };
  }, [infoMission?.duration?.dateend, listChangesMissionDetail]);

  const duplicateStartDate = duplicateStart ?? duplicateDefaults.start;
  const duplicateEndDate = duplicateEnd ?? duplicateDefaults.end;
  const duplicateDatesError =
    !duplicateStartDate || !duplicateEndDate
      ? "Les deux dates sont nécessaires."
      : calendarDayOf(duplicateEndDate) < calendarDayOf(duplicateStartDate)
        ? "La date de fin ne peut pas précéder la date de début."
        : "";

  const handleDuplicate = () => {
    setIsLoading(true);
    ApiDatabase.postDuplicateMission(
      {
        token,
        idMission: objID,
        dateStart: dateToIsoString(duplicateStartDate),
        dateEnd: dateToIsoString(duplicateEndDate),
        reason: checkResult?.duplicateReason || "other",
      },
      (data) => {
        toast.success(data.success || "Mission dupliquée");
        setModalDataReturn("mission updated");
        toggle();
      },
      (err) => {
        toast.error(err.response?.data?.message || "Erreur");
        setIsLoading(false);
      }
    );
  };

  // The mission that takes over: it starts the day the client loses its temp
  // worker — never in the past — and runs to the end the mission had. Proposed,
  // not imposed: the Dates step is where they are settled.
  const replacementDefaults = useMemo(() => {
    const periodChange = listChangesMissionDetail?.find(
      (change) => change.field === "period"
    );
    return getReplacementRange({
      endDate: parseCalendarDate(periodChange?.after?.end),
      missionDateEnd: infoMission?.duration?.dateend,
    });
  }, [listChangesMissionDetail, infoMission?.duration?.dateend]);

  const replacementStartDate = replacementStart ?? replacementDefaults.start;
  const replacementEndDate = replacementEnd ?? replacementDefaults.end;
  // Both are required: the api only duplicates when it receives the two, so a
  // missing end would confirm the interruption and create nothing, silently.
  const replacementDatesError =
    !replacementStartDate || !replacementEndDate
      ? "Les deux dates sont nécessaires."
      : calendarDayOf(replacementEndDate) < calendarDayOf(replacementStartDate)
        ? "La date de fin ne peut pas précéder la date de début."
        : "";

  // JBWEB-1089: the other changes of the operation are applied first, under
  // their usual rules; then the mission is interrupted on its new end, which
  // closes its contract on BeSTT with the reason's motif.
  const handleInterrupt = async () => {
    const selectedReason = closureReasons.find(
      (reason) => reason._id === interruptionReason
    );
    const periodChange = listChangesMissionDetail.find(
      (change) => change.field === "period"
    );
    const otherChanges = listChangesMissionDetail.filter(
      (change) => change.field !== "period"
    );
    const newEnd = parseCalendarDate(periodChange?.after?.end);
    const call = (method, payload) =>
      new Promise((resolve, reject) =>
        ApiDatabase[method](payload, resolve, reject)
      );

    setIsLoading(true);
    try {
      if (otherChanges.length > 0) {
        await call("postMissionDetail", {
          token,
          idMission: objID,
          generateContract: false,
          tempWorkerAlreadyAgreed: false,
          changes: otherChanges,
        });
      }
      const interruption = await call("updateMissionStatus", {
        idMission: objID,
        newStatus: "interrupted",
        reason: selectedReason?.name,
        reasonId: selectedReason?._id,
        comment: interruptionComment.trim() || undefined,
        endDate: dateToIsoString(newEnd),
        duplicateMission: replaceTempWorker,
        ...(replaceTempWorker && {
          duplicateDateStart: dateToIsoString(replacementStartDate),
          duplicateDateEnd: replacementEndDate
            ? dateToIsoString(replacementEndDate)
            : undefined,
        }),
      });
      updateMissionStatusLocal?.(objID, {
        status: "interrupted",
        reason: selectedReason?.name,
      });
      const done = replaceTempWorker
        ? "La mission a été interrompue, une nouvelle mission a été créée"
        : "La mission a été interrompue";
      if (isContractClosureIncomplete(interruption?.contractClosure)) {
        toast.warning(`${done}, ${CONTRACT_CLOSURE_INCOMPLETE}`);
      } else {
        toast.success(done);
      }
      setModalDataReturn("mission updated");
      toggle();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Erreur lors de l'interruption"
      );
      setIsLoading(false);
    }
  };

  const missionIsReplacement = (() => {
    const re = infoMission?.replacement_employee;
    return Boolean(
      re && (re.firstname || re.lastname || re.poste || re.description)
    );
  })();

  const duplicatedMissionBanner = infoMission?.replacement_mission ? (
    <Banner type="info">
      Cette mission a déjà été dupliquée (mission n°
      {infoMission.replacement_mission_info?.id_mission ||
        infoMission.replacement_mission}
      ).
    </Banner>
  ) : null;

  // Every screen below depends on whether the DPAE went out. Until BeSTT
  // answers, the modal holds its place rather than showing a refusal that is
  // only a wait.
  if (dpaePending) {
    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          Confirmer la modification
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4" aria-busy="true">
            <span className="h-4 w-3/4 skeleton-bar" />
            <ChangesCardSkeleton rows={listChangesMissionDetail?.length} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button size="LG" color="SECONDARY" onClick={toggle}>
            Annuler
          </Button>
          <Button size="LG" color="DISABLED">
            Confirmer
          </Button>
        </ModalFooter>
      </>
    );
  }

  // JBWEB-1089 (design): ending the mission early is confirmed in two steps —
  // the recap, where the end date sits apart in its own red card, then the
  // interruption itself: who asks, one of that side's reasons, an optional
  // comment, and whether the client wants the temp worker replaced.
  if (requiresInterruption) {
    const onRecap = step === "recap";
    const periodChange = listChangesMissionDetail.find(
      (change) => change.field === "period"
    );
    const otherChanges = listChangesMissionDetail.filter(
      (change) => change.field !== "period"
    );
    const otherOutcome = describeContractOutcome({
      mission: engagedMission,
      changes: otherChanges,
    });
    const otherCard = CHANGE_CARDS[cardKeyForOutcome(otherOutcome)];
    const newEnd = parseCalendarDate(periodChange?.after?.end);
    const endDateError = getInterruptEndDateError({
      endDate: newEnd,
      missionDateEnd: infoMission?.duration?.dateend,
    });
    const reasons = closureReasonsFor({
      reasons: closureReasons,
      requester: interruptionRequester,
      isReplacement: missionIsReplacement,
      withinTrialPeriod: isWithinTrialPeriod({
        endDate: newEnd,
        trialPeriodEnd: infoMission?.trial_period?.end,
      }),
    });
    const ready =
      Boolean(interruptionRequester) &&
      Boolean(interruptionReason) &&
      !endDateError;
    const onDates = replaceTempWorker && step === "dates";
    const steps = replaceTempWorker
      ? [...INTERRUPTION_STEPS, REPLACEMENT_DATES_STEP]
      : INTERRUPTION_STEPS;
    // Worth saying out loud: the replacement covers the very day the
    // interrupted mission ends, so the two share that day. On a regularisation
    // the start is pushed to today instead, and there is nothing to warn about.
    const startsOnInterruptionDay =
      replacementStartDate &&
      newEnd &&
      calendarDayOf(replacementStartDate) === calendarDayOf(newEnd);

    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          {onRecap
            ? "Confirmer la modification"
            : onDates
              ? "Mission de remplacement"
              : "Interrompre la mission"}
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              {onRecap
                ? "Ces modifications prendront effet à la confirmation et seront visibles côté entreprise et intérimaire."
                : onDates
                  ? "La mission qui prend le relais, sans intérimaire. Elle part sur les dates choisies ici."
                  : "Raccourcir la fin de mission d'un salarié attribué est juridiquement une rupture anticipée de la mission. Un motif est obligatoire."}
            </p>

            <StepPills
              steps={steps}
              current={step}
              onSelect={setStep}
            />

            {onRecap ? (
              <>
                {duplicatedMissionBanner}
                {periodChange && (
                  <ChangesCard
                    tone="danger"
                    icon={<Warning wh={16} color="#DC2626" />}
                    title="Interruption de la mission"
                    description="Un formulaire de rupture anticipée de la mission devra être rempli à l'étape suivante."
                    changes={[periodChange]}
                  />
                )}
                {otherChanges.length > 0 && (
                  <ChangesCard
                    icon={otherCard.icon}
                    title={otherCard.title}
                    changes={otherChanges}
                    mission={infoMission}
                  />
                )}
              </>
            ) : onDates ? (
              <>
                <DateField
                  label="Date de début"
                  value={replacementStartDate}
                  onChange={(date) => {
                    setReplacementStart(date);
                    // A start pushed past the end would leave an impossible
                    // period behind it.
                    if (
                      date &&
                      replacementEndDate &&
                      calendarDayOf(replacementEndDate) < calendarDayOf(date)
                    ) {
                      setReplacementEnd(date);
                    }
                  }}
                  hint={
                    startsOnInterruptionDay
                      ? "La mission débute le même jour."
                      : undefined
                  }
                />
                <DateField
                  label="Date de fin"
                  value={replacementEndDate}
                  onChange={setReplacementEnd}
                  minDate={replacementStartDate}
                  error={replacementDatesError}
                />
              </>
            ) : (
              <>
                <StyledInput
                  type="select"
                  label="Qui demande ?"
                  placeholder="Sélectionner"
                  value={interruptionRequester}
                  onChange={(e) => {
                    setInterruptionRequester(e.target.value);
                    // Each side has its own reasons: one chosen for the other
                    // side no longer applies.
                    setInterruptionReason("");
                  }}
                  list={INTERRUPTION_REQUESTERS}
                />
                <StyledInput
                  type="select"
                  label="Raison"
                  placeholder="Sélectionner"
                  value={interruptionReason}
                  onChange={(e) => setInterruptionReason(e.target.value)}
                  list={reasons.map((reason) => ({
                    value: reason._id,
                    label: reason.name,
                  }))}
                />
                <StyledInput
                  type="textarea"
                  label="Commentaire (optionnel)"
                  value={interruptionComment}
                  onChange={(e) => setInterruptionComment(e.target.value)}
                />
                {endDateError && (
                  <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg">
                    {endDateError}
                  </div>
                )}
                <CheckboxOptionCard
                  id="modal-replace-temp-worker"
                  checked={replaceTempWorker}
                  onChange={(e) => setReplaceTempWorker(e.target.checked)}
                  title="Remplacer l'intérimaire"
                  description="Crée une nouvelle mission identique à celle-ci pour y attribuer un autre intérimaire."
                />
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {isLoading ? (
            <>
              <Button size="LG" color="DISABLED">
                Annuler
              </Button>
              <Button size="LG" color="DISABLED">
                Interruption en cours ...
              </Button>
            </>
          ) : (
            <>
              <Button size="LG" color="SECONDARY" onClick={toggle}>
                Annuler
              </Button>
              {onRecap ? (
                <Button
                  size="LG"
                  color="PRIMARY"
                  onClick={() => setStep("interruption")}
                >
                  Suivant
                </Button>
              ) : onDates ? (
                <Button
                  size="LG"
                  color={replacementDatesError ? "DISABLED" : "PRIMARY"}
                  onClick={replacementDatesError ? undefined : handleInterrupt}
                >
                  Créer une mission
                </Button>
              ) : replaceTempWorker ? (
                <Button
                  size="LG"
                  color={ready ? "PRIMARY" : "DISABLED"}
                  onClick={ready ? () => setStep("dates") : undefined}
                >
                  Suivant
                </Button>
              ) : (
                <Button
                  size="LG"
                  color={ready ? "PRIMARY" : "DISABLED"}
                  onClick={ready ? handleInterrupt : undefined}
                >
                  Confirmer
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </>
    );
  }

  // JBWEB-1089 (design): a change that opens a prolongation request is confirmed
  // in two steps — the recap of everything that changes, then the prolongation
  // itself, where the admin says whether the temp worker already agreed.
  if (checkResult?.isAmendmentRequest) {
    const onRecap = step === "recap";
    const periodChange = listChangesMissionDetail.find(
      (change) => change.field === "period"
    );

    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          {onRecap ? "Confirmer la modification" : "Demande de prolongation"}
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              {onRecap
                ? "Ces modifications prendront effet à la confirmation et seront visibles côté entreprise et intérimaire."
                : checkResult.isReplacement
                  ? "La mission est un remplacement : elle se prolonge autant que nécessaire. Un avenant sera nécessaire pour confirmer ce changement."
                  : "La date de fin de la mission a été modifiée hors souplesse. Un avenant sera nécessaire pour confirmer ce changement."}
            </p>

            <StepPills
              steps={PROLONGATION_STEPS}
              current={step}
              onSelect={setStep}
            />

            {onRecap ? (
              <>
                {duplicatedMissionBanner}
                <ChangesCard
                  icon={<Signer wh={16} color="#374151" />}
                  title="Avenant requis"
                  changes={listChangesMissionDetail}
                  mission={infoMission}
                />
              </>
            ) : (
              <>
                {periodChange && (
                  <div className="bg-gray-100 rounded-md px-3.5 py-3">
                    <ChangeRow change={periodChange} />
                  </div>
                )}
                <CheckboxOptionCard
                  id="modal-temp-worker-already-agreed"
                  checked={tempWorkerAlreadyAgreed}
                  onChange={(e) => setTempWorkerAlreadyAgreed(e.target.checked)}
                  title="L'intérimaire a déjà accepté la prolongation"
                  description="À l'oral, par téléphone ou tout autre échange en dehors de l'application"
                />
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {isLoading ? (
            <>
              <Button size="LG" color="DISABLED">
                Annuler
              </Button>
              <Button size="LG" color="DISABLED">
                Modification de la mission ...
              </Button>
            </>
          ) : (
            <>
              <Button size="LG" color="SECONDARY" onClick={toggle}>
                Annuler
              </Button>
              {onRecap ? (
                <Button
                  size="LG"
                  color="PRIMARY"
                  onClick={() => setStep("prolongation")}
                >
                  Suivant
                </Button>
              ) : (
                <Button size="LG" color="PRIMARY" onClick={handleSubmit}>
                  Confirmer
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </>
    );
  }

  // JBWEB-1089 (design): a change the mission can no longer take — prolongations
  // capped, souplesse spent, 18 months — goes to a new mission. The card says why
  // and lists what would have changed. Re-inviting the temp worker is no longer
  // offered: the duplicate starts without one.
  if (canDuplicate) {
    const onRecap = step === "recap";

    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          {onRecap ? "Confirmer la modification" : "Dupliquer la mission"}
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              {onRecap
                ? "Ces modifications prendront effet à la confirmation et seront visibles côté entreprise et intérimaire."
                : "La nouvelle mission reprend celle-ci, sur les dates choisies ici."}
            </p>

            <StepPills
              steps={DUPLICATE_STEPS}
              current={step}
              onSelect={setStep}
            />

            {onRecap ? (
              <>
                {duplicatedMissionBanner}
                <ChangesCard
                  tone="danger"
                  icon={<Warning wh={16} color="#DC2626" />}
                  title="Nouvelle mission requise"
                  description={DUPLICATE_REASONS[checkResult.duplicateReason]}
                  changes={listChangesMissionDetail}
                  mission={infoMission}
                />
              </>
            ) : (
              <>
                <DateField
                  label="Date de début"
                  value={duplicateStartDate}
                  onChange={(date) => {
                    setDuplicateStart(date);
                    // A start pushed past the end would leave an impossible
                    // period behind it.
                    if (
                      date &&
                      duplicateEndDate &&
                      calendarDayOf(duplicateEndDate) < calendarDayOf(date)
                    ) {
                      setDuplicateEnd(date);
                    }
                  }}
                />
                <DateField
                  label="Date de fin"
                  value={duplicateEndDate}
                  onChange={setDuplicateEnd}
                  minDate={duplicateStartDate}
                  error={duplicateDatesError}
                />
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {isLoading ? (
            <>
              <Button size="LG" color="DISABLED">
                Annuler
              </Button>
              <Button size="LG" color="DISABLED">
                Duplication en cours ...
              </Button>
            </>
          ) : (
            <>
              <Button size="LG" color="SECONDARY" onClick={toggle}>
                Annuler
              </Button>
              {onRecap ? (
                <Button
                  size="LG"
                  color="PRIMARY"
                  onClick={() => setStep("dates")}
                >
                  Suivant
                </Button>
              ) : (
                <Button
                  size="LG"
                  color={duplicateDatesError ? "DISABLED" : "PRIMARY"}
                  onClick={duplicateDatesError ? undefined : handleDuplicate}
                >
                  Dupliquer la mission
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </>
    );
  }

  // JBWEB-1089 (design): what is left only needs confirming — applied to Jobbiz
  // alone, patched onto the BeSTT contract, turned into an amendment of value,
  // or refused. The card names which, and the sentence the check produced is
  // its description. One screen for all of them: the second one that used to
  // live here kept the pre-design layout alive for every case carrying a
  // banner, which is most of them.
  const cardKey = checkResult?.card ?? (canConfirm ? "immediate" : "refused");
  const card = CHANGE_CARDS[cardKey] ?? CHANGE_CARDS.immediate;
  // An amendment says it all in its title: the sentence under it only repeated
  // that one would be created, and the rows below already name what changes.
  const cardDescription =
    cardKey === "amendment"
      ? undefined
      : checkResult?.negativeFlexibility
        ? "Utilisation de la souplesse négative : la date de fin est réduite, le contrat BeSTT n'est pas modifié. La souplesse sera marquée comme utilisée."
        : checkResult?.notStarted
          ? "La mission n'a pas encore commencé : la date de fin est réduite et le contrat BeSTT est mis à jour."
          : banner?.message;

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Confirmer la modification
      </Modalheader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-500">
            {canConfirm
              ? "Ces modifications prendront effet à la confirmation et seront visibles côté entreprise et intérimaire."
              : "Cette modification ne peut pas être appliquée en l'état."}
          </p>
          {duplicatedMissionBanner}
          <ChangesCard
            tone={card.tone}
            icon={card.icon}
            title={card.title}
            description={cardDescription}
            changes={listChangesMissionDetail}
            mission={infoMission}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        {isLoading ? (
          <>
            <Button size="LG" color="DISABLED">
              Annuler
            </Button>
            <Button size="LG" color="DISABLED">
              Modification de la mission ...
            </Button>
          </>
        ) : (
          <>
            <Button size="LG" color="SECONDARY" onClick={toggle}>
              Annuler
            </Button>
            <Button
              size="LG"
              color={canConfirm ? "PRIMARY" : "DISABLED"}
              onClick={canConfirm ? handleSubmit : undefined}
            >
              Confirmer
            </Button>
          </>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalMissionDetailSubmit;
